/**
 * Notifications API client — typed wrappers around the four
 * `/notifications` endpoints used by the in-app notification center.
 *
 * Per `.claude/rules/frontend-data-flow.md` this module is the sole
 * gateway between the platform and the rest of the web stack — the
 * notifications slice and any future container (NotificationBell,
 * /account/notifications page) MUST go through these helpers, which
 * carry the envelope-unwrap + ApiError translation contract that the
 * shared `_envelope.ts` provides.
 *
 * SH-NOTIFY-001. No UI components are built in this ticket — consumers
 * depend on the exported slice (in `@dfq/shared/stores/notifications`)
 * and on the typed helpers below.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';

// ----------------------------------------------------------------------------
// Domain types
// ----------------------------------------------------------------------------

/**
 * Strict union of notification types emitted by the platform. Reused as
 * the `type` filter for {@link fetchNotificationsApi} so TypeScript
 * rejects unsupported filter values at compile time — there is exactly
 * one source of truth for the discriminator + filter set.
 */
export type NotificationType =
  | 'order_status'
  | 'prepress_issue'
  | 'quote_response'
  | 'system';

/**
 * A single notification row as returned by the platform. The shape mirrors
 * the wire envelope verbatim (snake_case keys) so the slice can store
 * notifications without a per-item adapter — downstream containers project
 * the snake_case fields directly into their UI capsules.
 */
export interface Notification {
  id: number;
  user_id: number;
  type: NotificationType;
  title: string;
  message: string;
  /** ISO 8601 timestamp at which the user marked the row read, or `null`. */
  read_at: string | null;
  /** Customer/admin-relative path the row links to, or `null` if none. */
  action_url: string | null;
  /**
   * ISO 8601 timestamp at which the row was created on the platform.
   * Surfaced so the NotificationBell capsule can render a "2 hours ago"
   * relative timestamp without an extra round-trip. Optional because
   * earlier consumers (shipped before SH-NOTIFY-002) didn't depend on it.
   */
  created_at?: string;
}

/**
 * Pagination metadata block returned alongside a notifications page.
 * Slice consumers store these scalars under `state.pagination` so the
 * NotificationBell / list view can decide whether to fetch more pages.
 */
export interface NotificationsPaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/**
 * `data` shape returned by `GET /api/v1/notifications`.
 */
export interface NotificationsListResponse {
  notifications: Notification[];
  meta: NotificationsPaginationMeta;
}

/**
 * Wire shapes — mirror the platform `NotificationDTO::toArray()` output and
 * the `ApiResponse` envelope verbatim (camelCase fields, `meta.pagination`,
 * `data.count`, `data.marked_count`). Adapters below project them into the
 * client-side {@link Notification} shape.
 */
export interface NotificationWire {
  id: number;
  userId: number;
  type: NotificationType;
  title: string;
  message: string;
  readAt: string | null;
  actionUrl: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NotificationsPaginationWire {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
}

export interface NotificationsListWire {
  data: { notifications: NotificationWire[] };
  meta: { pagination: NotificationsPaginationWire };
}

export function adaptNotification(wire: NotificationWire): Notification {
  return {
    id: wire.id,
    user_id: wire.userId,
    type: wire.type,
    title: wire.title,
    message: wire.message,
    read_at: wire.readAt,
    action_url: wire.actionUrl,
    ...(wire.createdAt !== null ? { created_at: wire.createdAt } : {}),
  };
}

export function adaptNotificationsPage(
  wire: NotificationsListWire,
): NotificationsListResponse {
  const { pagination } = wire.meta;
  return {
    notifications: wire.data.notifications.map(adaptNotification),
    meta: {
      current_page: pagination.page,
      last_page: pagination.last_page,
      per_page: pagination.per_page,
      total: pagination.total,
    },
  };
}

/**
 * Query parameters accepted by {@link fetchNotificationsApi}. Both fields
 * are optional — omitting them returns the first page across all types.
 */
export interface FetchNotificationsParams {
  page?: number;
  /**
   * Restrict results to one notification type. Strict union so callers
   * cannot pass an unsupported filter value at compile time.
   */
  type?: NotificationType;
}

/**
 * `data` shape returned by `GET /api/v1/notifications/unread-count`.
 */
export interface UnreadCountResponse {
  unread_count: number;
}

/**
 * `data` shape returned by `PUT /api/v1/notifications/:id/read`.
 */
export interface MarkNotificationReadResponse {
  id: number;
  read_at: string;
}

/**
 * `data` shape returned by `PUT /api/v1/notifications/read-all`.
 */
export interface MarkAllReadResponse {
  updated_count: number;
}

// ----------------------------------------------------------------------------
// Endpoint helpers
// ----------------------------------------------------------------------------

/**
 * `GET /api/v1/notifications` — paginated list of notifications for the
 * authenticated user, optionally filtered by type. Newest-first per the
 * platform contract.
 */
export async function fetchNotificationsApi(
  params: FetchNotificationsParams = {},
): Promise<NotificationsListResponse> {
  try {
    const response = await apiClient.get<ApiEnvelope<NotificationsListWire['data']>>(
      '/notifications',
      {
        params: {
          ...(params.page !== undefined ? { page: params.page } : {}),
          ...(params.type !== undefined ? { type: params.type } : {}),
        },
      },
    );
    const data = unwrap(response.data, response.status);
    const pagination = (
      response.data as unknown as { meta?: Partial<NotificationsListWire['meta']> }
    ).meta?.pagination;
    if (pagination === undefined) {
      throw new Error('Notifications response is missing meta.pagination');
    }
    return adaptNotificationsPage({ data, meta: { pagination } });
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * `GET /api/v1/notifications/unread-count` — lightweight unread-count
 * lookup intended for badge polling. Does not return notifications.
 */
export async function fetchUnreadCountApi(): Promise<UnreadCountResponse> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ count: number }>>(
      '/notifications/unread-count',
    );
    const data = unwrap(response.data, response.status);
    return { unread_count: data.count };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * `PUT /api/v1/notifications/:id/read` — mark a single notification as
 * read. Idempotent: re-marking an already-read notification still
 * succeeds with the existing `read_at` timestamp.
 */
export async function markNotificationReadApi(
  id: number,
): Promise<MarkNotificationReadResponse> {
  try {
    const response = await apiClient.put<
      ApiEnvelope<{ notification: NotificationWire }>
    >(`/notifications/${encodeURIComponent(String(id))}/read`);
    const { notification } = unwrap(response.data, response.status);
    return {
      id: notification.id,
      read_at: notification.readAt ?? new Date().toISOString(),
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * `PUT /api/v1/notifications/read-all` — mark every unread notification
 * for the authenticated user as read in one round-trip.
 */
export async function markAllReadApi(): Promise<MarkAllReadResponse> {
  try {
    const response = await apiClient.put<ApiEnvelope<{ marked_count: number }>>(
      '/notifications/read-all',
    );
    const data = unwrap(response.data, response.status);
    return { updated_count: data.marked_count };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Convenience namespace for callers that prefer `notificationsApi.fetch(...)`
 * over the named imports.
 */
export const notificationsApi = {
  fetch: fetchNotificationsApi,
  fetchUnreadCount: fetchUnreadCountApi,
  markRead: markNotificationReadApi,
  markAllRead: markAllReadApi,
};
